import React from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  StyleSheet,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { ChevronDown, X } from '../icons';
import { FormCompactPrimaryButton } from './FormCompactPrimaryButton';
import { MemberPickerChip } from './MemberPickerChip';
import { formDropdownTriggerPlaceholderLabelStyle } from './FormDropdown';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight } from '../../theme/typography';

export type CompactMemberPickerMember = { name?: string; email?: string };

function memberDisplayLabel(member: CompactMemberPickerMember): string {
  const name = member.name?.trim();
  const email = member.email?.trim();
  if (name && email && name !== email) {
    return name;
  }
  return name || email || '';
}

export function memberPickerEmailKey(member: CompactMemberPickerMember): string | null {
  const e = member.email?.trim();
  return e != null && e.length > 0 ? e : null;
}

/**
 * Single-line summary for the read-only picker field: up to two names, then "+N" for the rest.
 */
export function buildCompactMemberPickerDisplayLine(
  members: ReadonlyArray<CompactMemberPickerMember>,
): string {
  const labels = members.map(memberDisplayLabel).filter(Boolean);
  if (labels.length === 0) {
    return '';
  }
  if (labels.length <= 2) {
    return labels.join(', ');
  }
  const firstTwo = labels.slice(0, 2).join(', ');
  const rest = labels.length - 2;
  return `${firstTwo} +${rest}`;
}

/** Screen reader value: full count and names list (not truncated). */
export function buildCompactMemberPickerA11yValue(
  members: ReadonlyArray<CompactMemberPickerMember>,
): string {
  if (members.length === 0) {
    return 'None selected';
  }
  const labels = members.map(memberDisplayLabel).filter(Boolean);
  return `${members.length} selected: ${labels.join(', ')}`;
}

/** Safe for `testID` matching (avoids `@` / odd chars in matchers). */
function chipTestIdSuffix(email: string): string {
  return email.replace(/[^a-zA-Z0-9]/g, '_');
}

function pickerChevronProps(isDisabled: boolean): {
  size: number;
  color: string;
  style?: ViewStyle;
} {
  const color = isDisabled ? colors.textFormDisabled : colors.textSecondary;
  return {
    size: spacing.formDropdownChevronSize,
    color,
    ...(Platform.OS === 'web' ? { style: { color } as ViewStyle } : {}),
  };
}

export type FormCompactMemberPickerRowProps = {
  members: ReadonlyArray<CompactMemberPickerMember>;
  placeholder: string;
  onPress: () => void;
  disabled?: boolean;
  addLabel: string;
  editLabel: string;
  /** Shown in accessibility labels (e.g. Vertiv Sponsors). */
  fieldTitle: string;
  buttonTestID: string;
  inputTestID?: string;
  icon?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  /** `chips`: multiselect-style tokens inside the bordered field (see {@link FormDropdown} web multi). */
  summaryVariant?: 'inlineText' | 'chips';
  /** When true, primary button label stays {@link addLabel} even if there is a selection. */
  alwaysUseAddLabel?: boolean;
  /**
   * When {@link summaryVariant} is `chips`, show a trailing dropdown-style chevron that opens the picker.
   * Default false — Vertiv Sponsors is not a native dropdown; use the primary action button to open.
   */
  showTrailingChevron?: boolean;
  /** Remove one member by email; omit when the row is display-only. */
  onRemoveMember?: (email: string) => void;
  /** Clear all members; omit when the row is display-only. */
  onClearMembers?: () => void;
};

export const FormCompactMemberPickerRow: React.FC<FormCompactMemberPickerRowProps> = ({
  members,
  placeholder,
  onPress,
  disabled = false,
  addLabel,
  editLabel,
  fieldTitle,
  buttonTestID,
  inputTestID,
  icon,
  style,
  summaryVariant = 'inlineText',
  alwaysUseAddLabel = false,
  showTrailingChevron = false,
  onRemoveMember,
  onClearMembers,
}) => {
  const hasSelection = members.length > 0;
  const displayLine = buildCompactMemberPickerDisplayLine(members);
  const value = hasSelection ? displayLine : '';
  const actionLabel = alwaysUseAddLabel ? addLabel : hasSelection ? editLabel : addLabel;
  const a11ySummary = buildCompactMemberPickerA11yValue(members);
  const actionA11y = alwaysUseAddLabel
    ? `${actionLabel}. ${a11ySummary} for ${fieldTitle}. Opens member selector.`
    : hasSelection
      ? `${actionLabel}. ${a11ySummary} for ${fieldTitle}. Opens member selector.`
      : `${actionLabel}. ${fieldTitle}. Opens member selector.`;

  const showChipRemove = Boolean(onRemoveMember) && !disabled;
  const canClearAll = Boolean(onClearMembers) && !disabled && hasSelection;

  const chipField = summaryVariant === 'chips' ? (
    <View
      testID={inputTestID}
      style={[
        styles.chipFieldShell,
        Platform.OS === 'web' && styles.chipFieldShellWeb,
        disabled && styles.chipFieldDisabled,
      ]}
      importantForAccessibility="no-hide-descendants"
    >
      <ScrollView
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        style={styles.chipScroll}
        contentContainerStyle={styles.chipScrollContent}
      >
        {!hasSelection ? (
          <Pressable
            testID={inputTestID != null ? `${inputTestID}-placeholder` : 'member-picker-placeholder'}
            onPress={disabled ? undefined : onPress}
            disabled={disabled}
            style={styles.chipPlaceholderTap}
            accessibilityLabel={`${fieldTitle} selection`}
            accessibilityHint={`${placeholder}. Opens member selector.`}
            accessibilityRole="button"
          >
            <Text
              style={[
                formDropdownTriggerPlaceholderLabelStyle,
                disabled && styles.chipPlaceholderDisabled,
              ]}
              numberOfLines={1}
            >
              {placeholder}
            </Text>
          </Pressable>
        ) : (
          members.map((m, index) => {
            const emailKey = memberPickerEmailKey(m);
            const label = memberDisplayLabel(m) || emailKey || `Member ${String(index + 1)}`;
            const removable = showChipRemove && emailKey != null;
            return (
              <View
                key={emailKey ?? `${label}-${String(index)}`}
                style={styles.chipWrap}
              >
                <MemberPickerChip
                  label={label}
                  testID={
                    emailKey != null
                      ? `member-picker-chip-${chipTestIdSuffix(emailKey)}`
                      : `member-picker-chip-index-${String(index)}`
                  }
                  disabled={disabled}
                  onRemove={
                    removable
                      ? () => {
                          onRemoveMember?.(emailKey);
                        }
                      : undefined
                  }
                />
              </View>
            );
          })
        )}
      </ScrollView>
      {canClearAll ? (
        <Pressable
          onPress={() => {
            onClearMembers?.();
          }}
          style={({ pressed }) => [
            styles.chipClearButton,
            pressed && styles.chipClearButtonPressed,
          ]}
          hitSlop={spacing.sm}
          testID="member-picker-clear-all"
          accessibilityLabel={`Clear all ${fieldTitle}`}
          accessibilityRole="button"
          accessibilityHint="Removes every selected member"
        >
          <X size={14} color={colors.textSecondary} />
        </Pressable>
      ) : null}
      {showTrailingChevron ? (
        <Pressable
          onPress={disabled ? undefined : onPress}
          disabled={disabled}
          style={styles.chipChevronCell}
          accessibilityLabel={`Open ${fieldTitle} list`}
          accessibilityRole="button"
          testID="member-picker-open-chevron"
        >
          <ChevronDown {...pickerChevronProps(Boolean(disabled))} />
        </Pressable>
      ) : null}
    </View>
  ) : null;

  return (
    <View style={[styles.row, style]}>
      <FormCompactPrimaryButton
        onPress={onPress}
        disabled={disabled}
        accessibilityLabel={actionA11y}
        testID={buttonTestID}
        icon={icon}
        style={styles.button}
      >
        {actionLabel}
      </FormCompactPrimaryButton>
      {summaryVariant === 'chips' ? (
        chipField
      ) : (
        <TextInput
          testID={inputTestID}
          editable={false}
          pointerEvents="none"
          showSoftInputOnFocus={false}
          value={value}
          placeholder={placeholder}
          placeholderTextColor={disabled ? colors.textFormDisabled : colors.inputPlaceholder}
          accessibilityLabel={`${fieldTitle} selection`}
          accessibilityValue={{ text: a11ySummary }}
          multiline={false}
          numberOfLines={1}
          ellipsizeMode="tail"
          {...Platform.select({
            web: { readOnly: true as const },
            default: {},
          })}
          style={[
            styles.input,
            Platform.OS === 'web' && styles.inputWebSingleLine,
            disabled && styles.inputDisabled,
            hasSelection && !disabled && styles.inputReadonlyFilled,
          ]}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    width: '100%' as const,
  },
  button: {
    flexShrink: 0,
    minHeight: spacing.formControlHeight,
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    backgroundColor: colors.backgroundForm,
    color: colors.textPrimary,
    height: spacing.formControlHeight,
    maxHeight: spacing.formControlHeight,
    paddingVertical: spacing.formControlSingleLinePaddingVertical,
    ...Platform.select({
      web: {
        width: '100%' as const,
        outlineStyle: 'none' as const,
        outlineWidth: 0,
        boxSizing: 'border-box' as const,
        WebkitAppearance: 'none' as const,
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        whiteSpace: 'nowrap' as const,
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
      },
      default: {},
    }),
  },
  inputWebSingleLine: {
    ...Platform.select({
      web: {
        ':focus': {
          outlineWidth: 0,
          borderColor: colors.inputBorderFocus,
          boxShadow: colors.formControlFocusRing,
        },
        ':hover': {
          borderColor: colors.inputBorderInteractive,
        },
      },
      default: {},
    }),
  },
  /** Read-only value: keep primary text (not `disabled` grey). */
  inputReadonlyFilled: {
    color: colors.textPrimary,
    backgroundColor: colors.backgroundForm,
  },
  inputDisabled: {
    backgroundColor: colors.backgroundSecondary,
    color: colors.textFormDisabled,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as const,
        '::placeholder': {
          color: colors.textFormDisabled,
          opacity: 1,
        },
      },
      default: {},
    }),
  },
  chipFieldShell: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formDropdownBorderRadius,
    paddingLeft: spacing.formControlPaddingHorizontal,
    paddingRight: spacing.xs,
    minHeight: spacing.formControlHeight,
    backgroundColor: colors.backgroundForm,
    ...Platform.select({
      web: {
        outlineWidth: 0,
        boxSizing: 'border-box' as const,
      },
      default: {},
    }),
  },
  chipFieldShellWeb: {
    ...Platform.select({
      web: {
        ':focus-within': {
          borderWidth: 2,
          borderColor: colors.inputBorderInteractive,
        },
        ':hover': {
          borderColor: colors.inputBorderInteractive,
        },
      },
      default: {},
    }),
  },
  chipFieldDisabled: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    ...Platform.select({
      web: {
        ':hover': {
          borderColor: colors.inputBorderDisabled,
        },
        ':focus-within': {
          borderWidth: 1,
          borderColor: colors.inputBorderDisabled,
        },
      },
      default: {},
    }),
  },
  chipScroll: {
    flex: 1,
    minWidth: 0,
  },
  chipScrollContent: {
    alignItems: 'center',
    flexGrow: 1,
    paddingVertical: spacing.xs,
    paddingRight: spacing.xs,
    ...Platform.select({
      web: {
        gap: spacing.sm,
      },
      default: {},
    }),
  },
  chipWrap: {
    marginRight: spacing.sm,
    ...Platform.select({
      web: {
        marginRight: 0,
      },
      default: {},
    }),
  },
  chipPlaceholderTap: {
    flexGrow: 1,
    justifyContent: 'center',
    minHeight: spacing.formControlHeight - spacing.xs * 2,
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  chipPlaceholderDisabled: {
    color: colors.textFormDisabled,
  },
  chipClearButton: {
    padding: spacing.xs,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
      },
    }),
  },
  chipClearButtonPressed: {
    opacity: 0.55,
  },
  chipChevronCell: {
    padding: spacing.sm,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
      },
    }),
  },
});
